<template>
  <header class="gs-portal-header">
    <div class="gs-portal-header__bar">
      <div class="gs-portal-header__identity">
        <p class="gs-portal-header__eyebrow">Affiliate portal</p>
        <h1 class="gs-portal-header__title">{{ portalHeading }}</h1>
      </div>

      <div class="gs-portal-header__selects gs-refresh-controls">
        <gs-field
          v-if="showElements && hasRelatedFunnels"
          class="gs-portal-header__select"
          :label="$t('frontend_funnel')"
        >
          <gs-name-select
            v-model="localSelectedPromoLink.funnel"
            size="full"
            :placeholder="$t('frontend_funnel')"
            :options="funnelOptions"
            @change="$emit('changedFunnel', localSelectedPromoLink.funnel)"
          />
        </gs-field>

        <gs-field
          v-if="hasOtherResources"
          class="gs-portal-header__select"
          :label="portalFieldLabel"
        >
          <gs-name-select
            v-model="localSelectedPromoLink.permalink"
            size="full"
            :placeholder="$t('frontend_groovesell_promotional_tool')"
            :options="promoToolOptions"
            @change="$emit('changedPromoTool', localSelectedPromoLink.permalink)"
          />
        </gs-field>

        <div v-else-if="portalName" class="gs-portal-header__static">
          <span v-if="showElements" class="gs-portal-header__static-label">
            {{ $t('frontend_portal') }}
          </span>
          <span class="gs-portal-header__static-value">{{ portalName }}</span>
        </div>
      </div>

      <div v-if="showElements" class="gs-portal-header__account">
        <span class="gs-portal-header__account-name">
          {{ $t('frontend_resource_promo_not') }} {{ userName }}?
        </span>
        <gs-button variant="ghost" size="sm" @click="requestLogout()">
          <gs-icon name="logout" size="16" />
          {{ $t('frontend_logout') }}
        </gs-button>
      </div>
    </div>

    <div v-if="hasAssetsTab" class="gs-portal-header__section">
      <gs-button
        variant="outline"
        size="sm"
        @click="requestBackToNavigation()"
      >
        <gs-icon name="arrow-left" size="16" />
        {{ $t('frontend_resource_promo_back_to_groovesell') }}
      </gs-button>

      <h2 class="gs-portal-header__section-title">{{ currentSectionLabel }}</h2>

      <div
        v-if="isRegistered && shouldShowSwitches && localFunnelResource"
        class="gs-portal-header__done"
      >
        <span class="gs-portal-header__done-label">
          {{ $t('frontend_resource_promo_mark_as_completed') }}
        </span>
        <gd-toggle
          v-if="assetsTab == 'swipes'"
          v-model="localFunnelResource.affiliate_resource.swipes_completed"
          :value="localFunnelResource.affiliate_resource.swipes_completed"
          @change="toggleSwitchValue(1)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'blogReviews'"
          v-model="localFunnelResource.affiliate_resource.blog_reviews_completed"
          :value="localFunnelResource.affiliate_resource.blog_reviews_completed"
          @change="toggleSwitchValue(2)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'banners'"
          v-model="localFunnelResource.affiliate_resource.banners_completed"
          :value="localFunnelResource.affiliate_resource.banners_completed"
          @change="toggleSwitchValue(3)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'thankyouPageAds'"
          v-model="localFunnelResource.affiliate_resource.thankyou_page_ads_completed"
          :value="localFunnelResource.affiliate_resource.thankyou_page_ads_completed"
          @change="toggleSwitchValue(4)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'logoutPageAds'"
          v-model="localFunnelResource.affiliate_resource.logout_page_ads_completed"
          :value="localFunnelResource.affiliate_resource.logout_page_ads_completed"
          @change="toggleSwitchValue(5)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'facebookAds'"
          v-model="localFunnelResource.affiliate_resource.facebook_ads_completed"
          :value="localFunnelResource.affiliate_resource.facebook_ads_completed"
          @change="toggleSwitchValue(6)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'socialMediaPosts'"
          v-model="localFunnelResource.affiliate_resource.social_media_posts_completed"
          :value="localFunnelResource.affiliate_resource.social_media_posts_completed"
          @change="toggleSwitchValue(7)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'allArticles'"
          v-model="localFunnelResource.affiliate_resource.press_releases_completed"
          :value="localFunnelResource.affiliate_resource.press_releases_completed"
          @change="toggleSwitchValue(8)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'videoReviews'"
          v-model="localFunnelResource.affiliate_resource.video_reviews_completed"
          :value="localFunnelResource.affiliate_resource.video_reviews_completed"
          @change="toggleSwitchValue(9)"
        />
        <gd-toggle
          v-else-if="assetsTab == 'signatures'"
          v-model="localFunnelResource.affiliate_resource.signatures_completed"
          :value="localFunnelResource.affiliate_resource.signatures_completed"
          @change="toggleSwitchValue(10)"
        />
      </div>
    </div>
  </header>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    assetsTab: {
      type: String,
      default: '',
    },
    funnelResource: {
      type: Object,
      default: () => {},
    },
    relatedFunnels: {
      type: Array,
      default: () => [],
    },
    selectedPromoLink: {
      type: Object,
      default: () => {},
    },
    otherResources: {
      type: Array,
      default: () => [],
    },
    isRegistered: {
      type: Number,
      default: 1,
    },
    directAffiliateLinks: {
      type: Array,
      default: () => [],
    },
    assetsTabs: {
      type: Array,
      default: () => [],
    },
    userName: {
      type: String,
      default: '',
    },
    showElements: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localAssetsTabs: this.assetsTabs,
      localFunnelResource: this.funnelResource,
      localSelectedPromoLink: this.selectedPromoLink,
    };
  },
  computed: {
    funnelOptions() {
      return this.relatedFunnels.map(funnel => ({ value: funnel.id, text: funnel.name }));
    },
    promoToolOptions() {
      return this.otherResources.map(promoTool => ({ value: promoTool.permalink, text: promoTool.name }));
    },
    portalHeading() {
      if (!this.localFunnelResource) {
        return 'Affiliate portal';
      }

      return this.localFunnelResource.funnel_name || this.localFunnelResource.name || 'Affiliate portal';
    },
    portalFieldLabel() {
      return this.showElements ? this.$t('frontend_portal') : '';
    },
    portalName() {
      if (this.hasValidOtherResources) {
        return this.otherResources[0].name;
      }

      return this.localFunnelResource ? this.localFunnelResource.name : '';
    },
    currentSectionLabel() {
      const section = (this.assetsTabs || []).find(tab => tab.value === this.assetsTab);

      return section ? section.title : '';
    },
    hasAssetsTab() {
      return this.assetsTab != '';
    },
    hasRelatedFunnels() {
      return (
        this.localFunnelResource &&
        this.relatedFunnels &&
        this.relatedFunnels.length > 0
      );
    },
    hasOtherResources() {
      return (
        this.localFunnelResource &&
        this.otherResources &&
        this.otherResources.length > 1
      );
    },
    hasValidOtherResources() {
      return (
        this.localFunnelResource &&
        this.otherResources &&
        Array.isArray(this.otherResources) &&
        typeof this.otherResources[0] != 'undefined'
      );
    },
    shouldShowSwitches() {
      return (
        this.assetsTab != 'links' &&
        this.assetsTab != 'recruitmentLinks' &&
        this.assetsTab != 'stats'
      );
    },
  },
  methods: {
    requestBackToNavigation() {
      this.$emit('goBackToPromoToolsNavigation');
    },
    requestLogout() {
      this.$emit('logoutAffiliate');
    },
    async toggleSwitchValue(type) {
      let updatedValue = 1;

      if (type == 1) {
        updatedValue = this.localFunnelResource.affiliate_resource.swipes_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'swipes') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 2) {
        updatedValue = this.localFunnelResource.affiliate_resource.blog_reviews_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'blogReviews') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 3) {
        updatedValue = this.localFunnelResource.affiliate_resource.banners_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'banners') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 4) {
        updatedValue = this.localFunnelResource.affiliate_resource.thankyou_page_ads_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'thankyouPageAds') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 5) {
        updatedValue = this.localFunnelResource.affiliate_resource.logout_page_ads_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'logoutPageAds') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 6) {
        updatedValue = this.localFunnelResource.affiliate_resource.facebook_ads_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'facebookAds') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 7) {
        updatedValue = this.localFunnelResource.affiliate_resource.social_media_posts_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'socialMediaPosts') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 8) {
        updatedValue = this.localFunnelResource.affiliate_resource.press_releases_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'allArticles') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 9) {
        updatedValue = this.localFunnelResource.affiliate_resource.video_reviews_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'videoReviews') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      } else if (type == 10) {
        updatedValue = this.localFunnelResource.affiliate_resource.signatures_completed;

        var foundIndex = this.localAssetsTabs.findIndex((asset, index) => {
          if (asset.value == 'signatures') return true;
        });

        if (foundIndex != -1) {
          this.localAssetsTabs[foundIndex].completed = updatedValue;
        }
      }

      this.$emit('onToggleSwitchValue', {
        assetsTabs: this.localAssetsTabs,
        funnelResource: this.localFunnelResource,
      });

      await axios.post('/toggle-affiliate-funnel-resource-switch', {
        type,
        id: this.localFunnelResource.affiliate_resource.id,
        value: updatedValue,
      });
    },
  },
  watch: {
    funnelResource(newVal) {
      this.localFunnelResource = newVal;
    },
    assetsTabs(newVal) {
      this.localAssetsTabs = newVal;
    },
  },
};
</script>

<style scoped>
.gs-portal-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.gs-portal-header__bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 20px;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  padding: 16px 20px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-portal-header__identity {
  flex: 1 1 220px;
  min-width: 0;
}

.gs-portal-header__eyebrow {
  margin: 0 0 2px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-portal-header__title {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.gs-portal-header__selects {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.gs-portal-header__select {
  width: 360px;
  max-width: 100%;
}

.gs-portal-header__static {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-portal-header__static-label {
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-portal-header__static-value {
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 600;
}

.gs-portal-header__account {
  display: flex;
  flex: none;
  gap: 8px;
  align-items: center;
}

.gs-portal-header__account-name {
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-portal-header__section {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
  min-width: 0;
  padding: 12px 20px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-portal-header__section-title {
  flex: 1 1 auto;
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-portal-header__done {
  display: flex;
  flex: none;
  gap: 10px;
  align-items: center;
}

.gs-portal-header__done-label {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

@media (max-width: 640px) {
  .gs-portal-header__bar {
    gap: 12px;
    padding: 14px 16px;
  }

  .gs-portal-header__title {
    font-size: 18px;
  }

  .gs-portal-header__account {
    width: 100%;
    justify-content: space-between;
  }

  .gs-portal-header__select {
    width: 100%;
  }

  .gs-portal-header__selects {
    width: 100%;
  }

  .gs-portal-header__section {
    padding: 12px 16px;
  }

  .gs-portal-header__section-title {
    flex: 1 1 100%;
    order: -1;
    font-size: 16px;
  }
}
</style>
